/*!
 * c-slider — native scroll-snap carousel custom element.
 * Replaces Slick / Swiper. Pixel-tunable via data attributes + CSS custom properties.
 *
 * USAGE
 *   <c-slider
 *     data-slides-mobile="1"        // slides visible at <= 600px
 *     data-slides-tablet="2"        // slides visible at <= 1024px
 *     data-slides-desktop="4"       // slides visible at >= 1025px
 *     data-gap="16"                 // px gap between slides (default 16)
 *     data-dots="true"              // render pagination dots
 *     data-arrows="true"            // render built-in arrows (false if using custom)
 *     data-infinite="false"         // wrap to start from end (and vice-versa)
 *     data-autoplay="0"             // autoplay interval in ms (0 = off)
 *     data-pause-on-hover="true"
 *   >
 *     <ul class="c-slider__track">
 *       <li class="c-slider__slide">…</li>
 *       …
 *     </ul>
 *     <button class="c-slider__arrow c-slider__arrow--prev" aria-label="Previous">‹</button>
 *     <button class="c-slider__arrow c-slider__arrow--next" aria-label="Next">›</button>
 *     <ol class="c-slider__dots" aria-hidden="true"></ol>
 *   </c-slider>
 *
 * CUSTOM ARROWS:
 *   Place external elements with [data-c-slider-prev]/[data-c-slider-next] and a
 *   data-target="<slider-id>" attribute pointing to the c-slider id. Disable
 *   built-in arrows with data-arrows="false".
 */
(function () {
  if (window.customElements && customElements.get('c-slider')) return;

  class CSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('.c-slider__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (!this.slides.length) return;
      this.gap = parseInt(this.dataset.gap || '16', 10);
      this.infinite = this.dataset.infinite === 'true';
      this.autoplay = parseInt(this.dataset.autoplay || '0', 10);
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.slidesPerView = {
        mobile: parseFloat(this.dataset.slidesMobile || '1'),
        tablet: parseFloat(this.dataset.slidesTablet || '2'),
        desktop: parseFloat(this.dataset.slidesDesktop || '4'),
      };
      this._applyCssVars();
      window.addEventListener('resize', () => this._applyCssVars(), { passive: true });

      if (this.infinite) this._cloneEdges();
      if (this.dataset.dots === 'true') this._buildDots();
      this._wireArrows();
      this._wireExternalArrows();
      this._wireScrollListener();
      if (this.autoplay > 0) this._startAutoplay();
      this._announce();
    }

    _applyCssVars() {
      const w = window.innerWidth;
      let n;
      if (w <= 600) n = this.slidesPerView.mobile;
      else if (w <= 1024) n = this.slidesPerView.tablet;
      else n = this.slidesPerView.desktop;
      this.style.setProperty('--c-slider-per-view', n);
      this.style.setProperty('--c-slider-gap', this.gap + 'px');
    }

    _cloneEdges() {
      // Clone first N and last N slides so the user can scroll past edges seamlessly.
      // N = max(slidesPerView). For visual continuity, infinite mode jumps the scrollLeft
      // back to the "real" slide when a clone enters fully into view.
      const max = Math.ceil(Math.max(...Object.values(this.slidesPerView)));
      const realSlides = this.slides.slice();
      realSlides.slice(0, max).forEach(s => {
        const c = s.cloneNode(true);
        c.setAttribute('data-c-slider-clone', 'tail');
        this.track.appendChild(c);
      });
      realSlides.slice(-max).forEach(s => {
        const c = s.cloneNode(true);
        c.setAttribute('data-c-slider-clone', 'head');
        this.track.insertBefore(c, this.track.firstChild);
      });
      this._cloneCount = max;
      this._realCount = realSlides.length;
      // Set initial scroll to first real slide
      requestAnimationFrame(() => this._scrollToIndex(0, false));
    }

    _buildDots() {
      const dots = this.querySelector('.c-slider__dots') || document.createElement('ol');
      dots.classList.add('c-slider__dots');
      dots.setAttribute('aria-hidden', 'true');
      dots.innerHTML = '';
      const count = this._realCount || this.slides.length;
      for (let i = 0; i < count; i++) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c-slider__dot';
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.addEventListener('click', () => this._scrollToIndex(i, true));
        li.appendChild(btn);
        dots.appendChild(li);
      }
      if (!dots.parentNode) this.appendChild(dots);
      this.dotsEl = dots;
    }

    _wireArrows() {
      if (this.dataset.arrows === 'false') return;
      const prev = this.querySelector('.c-slider__arrow--prev');
      const next = this.querySelector('.c-slider__arrow--next');
      prev?.addEventListener('click', () => this._step(-1));
      next?.addEventListener('click', () => this._step(1));
    }

    _wireExternalArrows() {
      const id = this.id;
      if (!id) return;
      document.querySelectorAll(`[data-c-slider-prev][data-target="${id}"]`).forEach(b =>
        b.addEventListener('click', () => this._step(-1))
      );
      document.querySelectorAll(`[data-c-slider-next][data-target="${id}"]`).forEach(b =>
        b.addEventListener('click', () => this._step(1))
      );
    }

    _step(dir) {
      const slideW = this._getSlideWidth();
      this.track.scrollBy({ left: dir * slideW, behavior: 'smooth' });
    }

    _getSlideWidth() {
      const first = this.slides[0];
      if (!first) return 0;
      return first.getBoundingClientRect().width + this.gap;
    }

    _scrollToIndex(realIndex, smooth) {
      const offset = (this._cloneCount || 0) + realIndex;
      const slide = this.track.children[offset];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    }

    _wireScrollListener() {
      let raf = 0;
      this.track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          this._updateActive();
          if (this.infinite) this._maybeWrap();
        });
      }, { passive: true });
    }

    _updateActive() {
      const slideW = this._getSlideWidth();
      const idx = Math.round(this.track.scrollLeft / slideW);
      const realIdx = this.infinite ? ((idx - this._cloneCount + this._realCount) % this._realCount) : idx;
      if (this.dotsEl) {
        this.dotsEl.querySelectorAll('.c-slider__dot').forEach((d, i) => {
          d.classList.toggle('is-active', i === realIdx);
        });
      }
      this.dispatchEvent(new CustomEvent('slidechange', { detail: { index: realIdx } }));
    }

    _maybeWrap() {
      const slideW = this._getSlideWidth();
      const total = this.track.children.length;
      const idx = Math.round(this.track.scrollLeft / slideW);
      if (idx <= 0) {
        this.track.scrollTo({ left: this._realCount * slideW, behavior: 'auto' });
      } else if (idx >= total - this._cloneCount) {
        this.track.scrollTo({ left: this._cloneCount * slideW, behavior: 'auto' });
      }
    }

    _startAutoplay() {
      this._timer = setInterval(() => this._step(1), this.autoplay);
      if (this.pauseOnHover) {
        this.addEventListener('pointerenter', () => clearInterval(this._timer));
        this.addEventListener('pointerleave', () => {
          this._timer = setInterval(() => this._step(1), this.autoplay);
        });
      }
    }

    _announce() {
      this.setAttribute('role', 'region');
      if (!this.hasAttribute('aria-roledescription')) {
        this.setAttribute('aria-roledescription', 'carousel');
      }
    }
  }

  customElements.define('c-slider', CSlider);
})();
